import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { displayCurrencyNumber, fromDisplayCurrency } from "../utils/pricing";

export default function Organisation() {
    const { organisation, setOrganisation } = useAuth();
    const [name, setName] = useState(organisation?.name ?? "");
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setName(organisation?.name ?? "");
    }, [organisation?.name]);

    useEffect(() => {
        // Keep settings in RAW backend units; every dollar render on this
        // page routes through ``displayCurrency`` and every dollar input
        // through ``displayCurrencyNumber`` / ``fromDisplayCurrency``.
        api.get("/org/settings")
            .then((r) => setSettings(r.data))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    const saveName = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/org/current", { name });
            setOrganisation(data);
            toast.success("Organisation updated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const saveSettings = async () => {
        setBusy(true);
        try {
            // settings are held in RAW backend units; ship as-is.
            const { data } = await api.put("/org/settings", settings);
            setSettings(data);
            toast.success("Budgets saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="organisation-page" className="space-y-10">
            <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                    Admin
                </div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">
                    Organisation
                </h1>
                <p className="mt-1.5 text-sm text-neutral-500">
                    Update the identity and default budgets for your workspace.
                </p>
            </div>

            <section className="rounded-lg border border-neutral-200 bg-white p-6">
                <h2 className="text-sm font-semibold text-neutral-900">Identity</h2>
                <div className="mt-4 max-w-md space-y-4">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Organisation name</Label>
                        <Input
                            data-testid="org-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <Button
                        onClick={saveName}
                        disabled={busy || name.trim() === (organisation?.name ?? "")}
                        data-testid="org-name-save"
                        className="bg-neutral-900 text-white hover:bg-neutral-800"
                    >
                        Save
                    </Button>
                </div>
            </section>

            <section className="rounded-lg border border-neutral-200 bg-white p-6">
                <h2 className="text-sm font-semibold text-neutral-900">Default budgets</h2>
                <p className="mt-1 text-xs text-neutral-500">
                    Values in USD. Applied to new modules unless overridden.
                </p>
                {settings && (
                    <div className="mt-4 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Trainer budget</Label>
                            <Input
                                data-testid="trainer-budget-input"
                                type="number"
                                min="0"
                                step="1"
                                value={displayCurrencyNumber(settings.trainer_budget)}
                                onChange={(e) =>
                                    setSettings({ ...settings, trainer_budget: fromDisplayCurrency(Number(e.target.value)) })
                                }
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Cinematic clip budget</Label>
                            <Input
                                data-testid="runway-budget-input"
                                type="number"
                                min="0"
                                step="1"
                                value={displayCurrencyNumber(settings.runway_budget)}
                                onChange={(e) =>
                                    setSettings({ ...settings, runway_budget: fromDisplayCurrency(Number(e.target.value)) })
                                }
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Button
                                onClick={saveSettings}
                                disabled={busy}
                                data-testid="settings-save"
                                className="bg-neutral-900 text-white hover:bg-neutral-800"
                            >
                                Save budgets
                            </Button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
